import { http, createConfig } from 'wagmi';
import { bsc, bscTestnet, mainnet, sepolia, polygon } from 'wagmi/chains';
import { walletConnect } from 'wagmi/connectors';

/**
 * WalletConnect Project ID
 *
 * IMPORTANT: This is hardcoded here to ensure consistency across all environments.
 * Get your Project ID at: https://cloud.reown.com or https://cloud.walletconnect.com
 *
 * Current Project ID: ee35dbf49aebc9f7678bafb8c883706d
 * Manage at: https://cloud.reown.com
 */
export const projectId = 'ee35dbf49aebc9f7678bafb8c883706d';

// Validate project ID
const isValidProjectId = projectId && projectId.length > 20;

// Warn if project ID is not set or is the placeholder
if (!isValidProjectId) {
  console.error(
    '\n' +
    '╔═══════════════════════════════════════════════════════════════════╗\n' +
    '║  ⚠️  WALLETCONNECT NOT CONFIGURED - WALLETCONNECT WILL NOT WORK!  ║\n' +
    '╚═══════════════════════════════════════════════════════════════════╝\n' +
    '\n' +
    '❌ Issue: WalletConnect Project ID is missing or invalid\n' +
    '\n' +
    '📋 To fix this:\n' +
    '  1. Go to: https://cloud.walletconnect.com\n' +
    '  2. Sign up/Sign in (free account)\n' +
    '  3. Create a new project\n' +
    '  4. Copy your Project ID\n' +
    '  5. Update the projectId in src/web3/config.ts\n' +
    '  6. Restart dev server: yarn dev\n' +
    '\n' +
    '⚠️  Users will NOT be able to connect wallets!\n'
  );
}

/**
 * Metadata for WalletConnect and Web3Modal
 */
export const metadata = {
  name: 'Farcana',
  description: 'Farcana Web3 Platform',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://farlabs.sakura.exchange',
  icons: ['https://avatars.githubusercontent.com/u/37784886'],
};

/**
 * Supported blockchain networks
 *
 * Primary Network:
 * - BSC (Binance Smart Chain) - ChainID: 56 (MAIN)
 *
 * Secondary Networks:
 * - Polygon - ChainID: 137
 * - Ethereum Mainnet - ChainID: 1
 *
 * Testnet Networks (for development):
 * - BSC Testnet - ChainID: 97
 * - Ethereum Sepolia - ChainID: 11155111
 */
export const chains = [
  bsc,           // Mainnet: BSC (PRIMARY - must be first)
  polygon,       // Mainnet: Polygon
  mainnet,       // Mainnet: Ethereum
  bscTestnet,    // Testnet: BSC
  sepolia,       // Testnet: Ethereum Sepolia
] as const;

/**
 * Default chain for new connections
 * BSC Mainnet is the primary network for Farcana
 */
export const defaultChain = bsc;

/**
 * Wagmi configuration for Web3Modal
 *
 * Web3Modal will automatically detect and support:
 * - Binance Wallet (browser extension)
 * - MetaMask (browser extension)
 * - Trust Wallet (mobile via WalletConnect)
 * - 300+ other wallets
 *
 * Wallet order is configured in Web3Modal initialization (src/main.tsx)
 */
export const config = createConfig({
  chains,
  connectors: [
    // WalletConnect v2 - Web3Modal will handle UI and wallet detection
    // Note: MetaMask and other browser extensions are auto-detected via EIP-6963
    walletConnect({
      projectId,
      metadata,
      showQrModal: false, // Web3Modal handles the modal UI
    }),
  ],
  transports: {
    [bsc.id]: http(),
    [polygon.id]: http(),
    [bscTestnet.id]: http(),
    [sepolia.id]: http(),
    [mainnet.id]: http(),
  },
});

/**
 * Chain metadata for UI display
 */
export const chainMetadata = {
  [bsc.id]: {
    name: 'BSC Mainnet',
    shortName: 'BSC',
    icon: '🟡',
    type: 'mainnet' as const,
    explorer: 'https://bscscan.com',
  },
  [polygon.id]: {
    name: 'Polygon Mainnet',
    shortName: 'Polygon',
    icon: '🟣',
    type: 'mainnet' as const,
    explorer: 'https://polygonscan.com',
  },
  [bscTestnet.id]: {
    name: 'BSC Testnet',
    shortName: 'BSC Test',
    icon: '🟡',
    type: 'testnet' as const,
    explorer: 'https://testnet.bscscan.com',
  },
  [sepolia.id]: {
    name: 'Ethereum Sepolia',
    shortName: 'Sepolia',
    icon: '⚪',
    type: 'testnet' as const,
    explorer: 'https://sepolia.etherscan.io',
  },
  [mainnet.id]: {
    name: 'Ethereum Mainnet',
    shortName: 'Ethereum',
    icon: '⚪',
    type: 'mainnet' as const,
    explorer: 'https://etherscan.io',
  },
} as const;

/**
 * Helper to get chain info by ID
 */
export function getChainInfo(chainId: number) {
  return chainMetadata[chainId as keyof typeof chainMetadata] || {
    name: 'Unknown Network',
    shortName: 'Unknown',
    icon: '❓',
    type: 'unknown' as const,
    explorer: '',
  };
}

/**
 * Check if chain is supported
 */
export function isSupportedChain(chainId: number): boolean {
  return chainId in chainMetadata;
}

/**
 * Get RPC URL for a chain
 */
export function getRpcUrl(chainId: number): string {
  const chain = chains.find(c => c.id === chainId);
  return chain?.rpcUrls.default.http[0] || '';
}

/**
 * Get block explorer URL for a chain
 */
export function getExplorerUrl(chainId: number): string {
  return chainMetadata[chainId as keyof typeof chainMetadata]?.explorer || '';
}

/**
 * Format transaction hash link for explorer
 */
export function getExplorerTxUrl(chainId: number, txHash: string): string {
  const explorerUrl = getExplorerUrl(chainId);
  return explorerUrl ? `${explorerUrl}/tx/${txHash}` : '';
}

/**
 * Format address link for explorer
 */
export function getExplorerAddressUrl(chainId: number, address: string): string {
  const explorerUrl = getExplorerUrl(chainId);
  return explorerUrl ? `${explorerUrl}/address/${address}` : '';
}
